<template>
  <ion-page>
    <PageHeaderWithMenu title="Разделы тренингов" :scrolled="headerScrolled" />
    <ion-content :class="[isMobile ? 'main-background' : 'liga-web-background']" :fullscreen="true" @ionScroll="onContentScroll">
      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Добавить раздел</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-item>
            <ion-input label="Название (для пользователей)" label-placement="stacked" v-model="newSection.name"
              placeholder="Например: Академия"></ion-input>
          </ion-item>
          <ion-item>
            <ion-select label="Роль доступа" label-placement="stacked" v-model="newSection.roleName"
              interface="popover" mode="ios" placeholder="Выберите роль">
              <ion-select-option v-for="a in allAuthorities" :key="a.name" :value="a.name">{{ a.description || a.name }}</ion-select-option>
            </ion-select>
          </ion-item>
          <ion-item>
            <ion-input label="Ключ папки (латиница)" label-placement="stacked" v-model="newSection.folderKey"
              placeholder="Academia, CoachGP, Ted"></ion-input>
          </ion-item>
          <ion-button expand="block" @click="addSection()" :disabled="!canAddSection">Добавить раздел</ion-button>
        </ion-card-content>
      </ion-card>

      <ion-card class="admin-card">
        <ion-card-header>
          <ion-card-title>Список разделов</ion-card-title>
        </ion-card-header>
        <ion-card-content>
          <ion-list v-if="sections.length">
            <ion-item v-for="s in sections" :key="s.id || s.folderKey" class="section-item">
              <ion-label>
                <h2>{{ s.name }}</h2>
                <p>Роль: {{ s.roleName }} · Папка: {{ s.folderKey }}</p>
              </ion-label>
              <ion-button fill="clear" color="medium" size="small" @click="renameSection(s)" v-if="s.id" title="Переименовать">
                <ion-icon slot="icon-only" :icon="pencilOutline"></ion-icon>
              </ion-button>
              <ion-button fill="clear" color="danger" @click="deleteSection(s)" v-if="s.id">Удалить</ion-button>
            </ion-item>
          </ion-list>
          <p v-else class="empty-hint">Разделов пока нет или загрузка с сервера не реализована.</p>
        </ion-card-content>
      </ion-card>
    </ion-content>
  </ion-page>
</template>

<script lang="ts">
import { Authority } from '@/model/authority.model';
import { EducationSection } from '@/model/education-section.model';
import { GlobalContext } from '@/model/globalcontext.model';
import AdminService from '@/services/admin.service';
import { CapacitorHttp } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonContent,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonPage,
  IonSelect,
  IonSelectOption,
  alertController,
  toastController,
} from '@ionic/vue';
import { pencilOutline } from 'ionicons/icons';
import PageHeaderWithMenu from '@/components/PageHeaderWithMenu.vue';
import axios from 'axios';
import { defineComponent, inject } from 'vue';

export default defineComponent({
  name: 'AdminEducationSections',
  components: {
    IonButton,
    IonCard,
    IonCardContent,
    IonCardHeader,
    IonCardTitle,
    IonContent,
    IonIcon,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonPage,
    IonSelect,
    IonSelectOption,
    PageHeaderWithMenu,
  },
  setup() {
    const globalContext = inject('globalContext') as GlobalContext;
    return { isMobile: globalContext.isMobile, pencilOutline };
  },
  data() {
    return {
      adminService: new AdminService(),
      allAuthorities: [] as Authority[],
      sections: [] as EducationSection[],
      newSection: {
        name: '',
        roleName: '',
        folderKey: '',
      },
      headerScrolled: false,
    };
  },
  computed: {
    canAddSection(): boolean {
      return !!(this.newSection.name?.trim() && this.newSection.roleName && this.newSection.folderKey?.trim());
    },
  },
  mounted() {
    this.loadAuthorities();
    this.loadSections();
  },
  ionViewDidEnter() {
    this.loadAuthorities();
    this.loadSections();
  },
  watch: {
    '$store.getters.rolesUpdatedAt'() {
      this.loadAuthorities();
    },
  },
  methods: {
    onContentScroll(e: CustomEvent) {
      const scrollTop = e.detail?.scrollTop ?? 0;
      this.headerScrolled = scrollTop > 16;
    },
    async loadAuthorities() {
      try {
        const token = (await Preferences.get({ key: 'jwt' })).value;
        const url = `${process.env.VUE_APP_API_URL}api/authorities-full`;
        if (this.isMobile && token) {
          const res = await CapacitorHttp.get({ url, headers: { Authorization: `Bearer ${token}` }, readTimeout: 60000 });
          this.allAuthorities = (res.data || []).sort((a: Authority, b: Authority) =>
            (a.description || '').localeCompare(b.description || ''));
        } else {
          const { data } = await axios.get<Authority[]>('api/authorities-full');
          this.allAuthorities = (data || []).sort((a, b) => (a.description || '').localeCompare(b.description || ''));
        }
      } catch (e) {
        console.error('loadAuthorities', e);
      }
    },
    async loadSections() {
      try {
        this.sections = await this.adminService.getEducationSections();
      } catch (e) {
        console.error('loadSections', e);
      }
    },
    async addSection() {
      if (!this.canAddSection) return;
      try {
        await this.adminService.createEducationSection({
          name: this.newSection.name.trim(),
          roleName: this.newSection.roleName,
          folderKey: this.newSection.folderKey.trim(),
        });
        const toast = await toastController.create({
          message: 'Раздел добавлен',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.newSection = { name: '', roleName: '', folderKey: '' };
        this.loadSections();
      } catch (e) {
        console.error('addSection', e);
        const toast = await toastController.create({
          message: 'Ошибка добавления раздела',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
    async renameSection(s: EducationSection) {
      const sectionId = s.id;
      if (!sectionId) return;
      const alert = await alertController.create({
        header: 'Переименовать раздел',
        cssClass: 'alert-dark-theme',
        inputs: [
          {
            name: 'name',
            type: 'text',
            value: s.name,
            placeholder: 'Название для пользователей',
          },
        ],
        buttons: [
          { text: 'Отмена', role: 'cancel' },
          {
            text: 'Сохранить',
            handler: async (data: { name?: string }) => {
              const newName = (data.name || '').trim();
              if (!newName) {
                const toast = await toastController.create({
                  message: 'Название не может быть пустым.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
                return false;
              }
              if (newName === s.name) return true;
              try {
                await this.adminService.updateEducationSection(sectionId, {
                  name: newName,
                  roleName: s.roleName,
                  folderKey: s.folderKey,
                });
                const toast = await toastController.create({
                  message: 'Название раздела обновлено.',
                  duration: 2000,
                  position: 'top',
                  color: 'success',
                });
                await toast.present();
                this.loadSections();
              } catch (e) {
                console.error('renameSection', e);
                const toast = await toastController.create({
                  message: 'Ошибка при переименовании.',
                  duration: 2000,
                  position: 'top',
                  color: 'danger',
                });
                await toast.present();
              }
              return true;
            },
          },
        ],
      });
      await alert.present();
    },
    async deleteSection(s: EducationSection) {
      if (!s.id) return;
      try {
        await this.adminService.deleteEducationSection(s.id);
        const toast = await toastController.create({
          message: 'Раздел удалён',
          duration: 2000,
          position: 'top',
          color: 'success',
        });
        await toast.present();
        this.loadSections();
      } catch (e) {
        console.error('deleteSection', e);
        const toast = await toastController.create({
          message: 'Ошибка удаления раздела',
          duration: 3000,
          position: 'top',
          color: 'danger',
        });
        await toast.present();
      }
    },
  },
});
</script>

<style scoped>
.admin-card {
  margin: 16px;
  --background: var(--ion-color-dark);
}

.admin-card ion-card-title {
  color: white;
}

.admin-card ion-card-content {
  color: rgba(255, 255, 255, 0.85);
}

.admin-card ion-item {
  --background: transparent;
  --color: white;
}

.admin-card ion-list {
  --background: transparent !important;
  background: transparent !important;
}

.section-item {
  --background: rgba(255, 255, 255, 0.05) !important;
  margin-bottom: 8px;
  color: white;
}

.section-item ion-label h2 {
  color: white;
}

.section-item ion-label p {
  color: rgba(255, 255, 255, 0.7);
}

.empty-hint {
  color: rgba(255, 255, 255, 0.6);
}

.header-title {
  letter-spacing: 0.08em;
}

/* Кнопки в стиле приложения: чёрно-оранжевый */
.admin-card ion-button:not([fill="clear"]) {
  --background: var(--ion-color-orange);
  --background-activated: var(--ion-color-orange-tint);
  --background-hover: var(--ion-color-orange-tint);
  --color: white;
  --color-activated: white;
  font-weight: bold;
  --border-radius: 14px;
  margin-top: 8px;
}

.admin-card ion-button[fill="clear"]:not([color="danger"]) {
  --color: var(--ion-color-orange);
  --color-activated: var(--ion-color-orange-tint);
  font-weight: 600;
}

.admin-card ion-button[fill="clear"][color="danger"] {
  --color-activated: var(--ion-color-danger-tint);
}
</style>
